/**
 * liveDocLoadTracker — pure state helpers for the spine's per-section
 * "loading into Word…" tracker.
 *
 * After a redraft the section's new text is committed to the server SSOT and
 * the add-in re-projects just that section into the live Word document
 * (``reinsertModuleIntoLiveDoc`` → ``insertSectionContent``). That write is
 * async, can be deferred while a Load is still settling, and can silently
 * no-op when the section's heading isn't present in the open doc. This tracker
 * makes the write observable on the spine row: ``loading`` while the write is
 * in flight, a transient ``loaded`` tick on a confirmed write
 * (``sectionsWritten > 0``) that self-expires after ``LIVE_DOC_LOADED_TTL_MS``,
 * and ``failed`` when nothing was written (heading not found / doc not Loaded)
 * or the write threw.
 *
 * Expiry is driven by the spine's existing 1s tick (which ``hasActiveLiveDocLoad``
 * already gates) rather than a per-section ``setTimeout``: no timer handles to
 * leak on unmount, and no race when a section re-enters ``loading`` inside the
 * confirmation window.
 *
 * The state is a plain ``Map`` owned by the shell and passed into ``LoomSpine``
 * as a prop — deliberately NOT stored on ``SpineSection``, so it is immune to
 * the SSE ``section_state_changed`` subline-wipe and to spine rebuilds. These
 * helpers are pure (return a new Map; never mutate the input) so the
 * transitions are unit-testable without rendering the shell.
 */

export type LiveDocLoadState = "loading" | "loaded" | "failed";

/** How long the transient "loaded into Word" confirmation stays on the row.
 *  Long enough to notice on a section that lands while the writer is reading
 *  elsewhere, short enough that a 38-section run doesn't end up a wall of
 *  ticks. */
export const LIVE_DOC_LOADED_TTL_MS = 5000;

export interface LiveDocLoadEntry {
  state: LiveDocLoadState;
  /** Wall-clock ms when the CURRENT loading attempt started; drives the spine
   *  elapsed timer, and is preserved into the ``failed`` state so the row can
   *  still show how long it tried. */
  startedAt: number;
}

export type LiveDocLoadMap = ReadonlyMap<string, LiveDocLoadEntry>;

/** Mark ``sectionId`` as loading into Word, stamping the attempt start. */
export function setLiveDocLoading(map: LiveDocLoadMap, sectionId: string, now: number): LiveDocLoadMap {
  const next = new Map(map);
  next.set(sectionId, { state: "loading", startedAt: now });
  return next;
}

/** Clear ``sectionId`` (a confirmed successful write, or a fresh regen/Load). */
export function clearLiveDocLoad(map: LiveDocLoadMap, sectionId: string): LiveDocLoadMap {
  if (!map.has(sectionId)) return map;
  const next = new Map(map);
  next.delete(sectionId);
  return next;
}

/** Mark ``sectionId`` as freshly written into Word. ``startedAt`` is re-stamped
 *  to NOW — for this state it is the moment the tick appeared, and it is what
 *  ``expireLiveDocLoaded`` measures the TTL from (not an elapsed reading). */
export function setLiveDocLoaded(map: LiveDocLoadMap, sectionId: string, now: number): LiveDocLoadMap {
  const next = new Map(map);
  next.set(sectionId, { state: "loaded", startedAt: now });
  return next;
}

/** Drop every ``loaded`` tick older than ``ttlMs``.
 *
 *  Returns the SAME map reference when nothing expired, so the shell can call
 *  this on every tick and React still bails out of the re-render. ``loading``
 *  and ``failed`` entries are never expired — they are resolved by their own
 *  write completing, not by time. */
export function expireLiveDocLoaded(
  map: LiveDocLoadMap,
  now: number,
  ttlMs: number = LIVE_DOC_LOADED_TTL_MS,
): LiveDocLoadMap {
  let expired: string[] | null = null;
  for (const [sectionId, entry] of map) {
    if (entry.state === "loaded" && now - entry.startedAt >= ttlMs) {
      (expired ??= []).push(sectionId);
    }
  }
  if (!expired) return map;
  const next = new Map(map);
  for (const sectionId of expired) next.delete(sectionId);
  return next;
}

/** Mark ``sectionId`` as failed, preserving the original ``startedAt`` when a
 *  loading entry exists so the elapsed reading stays stable across the flip. */
export function setLiveDocFailed(map: LiveDocLoadMap, sectionId: string, now: number): LiveDocLoadMap {
  const prev = map.get(sectionId);
  const next = new Map(map);
  next.set(sectionId, { state: "failed", startedAt: prev?.startedAt ?? now });
  return next;
}

/** True when any section is loading OR showing an unexpired ``loaded`` tick —
 *  used to keep the spine's elapsed-timer tick running even when no run is
 *  actively streaming. ``loaded`` counts because that same tick is what expires
 *  the confirmation; without it the last tick of a run would stick forever. */
export function hasActiveLiveDocLoad(map: LiveDocLoadMap): boolean {
  for (const entry of map.values()) {
    if (entry.state === "loading" || entry.state === "loaded") return true;
  }
  return false;
}

/** Classify a ``reinsertModuleIntoLiveDoc`` result into the tracker outcome.
 *
 *  ``sectionsWritten > 0`` is a success. Zero is a FAILURE only when writes were
 *  actually attempted — a module that produced no write jobs at all had nothing
 *  to project and is a no-op, not a failure. That distinction matters for a
 *  parent section whose prose all routes to declared children: it contributes no
 *  job of its own, and reporting it red told the writer something had broken
 *  when nothing had.
 *
 *  ``sectionsAttempted`` is optional so the pre-existing single-argument callers
 *  keep their exact behaviour; omitting it means "assume a write was attempted".
 */
export function outcomeFromSectionsWritten(
  sectionsWritten: number,
  sectionsAttempted?: number,
): "success" | "failed" | "noop" {
  if (sectionsWritten > 0) return "success";
  return sectionsAttempted === 0 ? "noop" : "failed";
}
